import { useRef, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@nexus/ui";
import { FileSpreadsheet, Upload } from "lucide-react";

interface ImportUploadCardProps {
  canParse: boolean;
  isParsing: boolean;
  onFileSelect: (file: File) => void;
}

export function ImportUploadCard({ canParse, isParsing, onFileSelect }: ImportUploadCardProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDisabled = !canParse || isParsing;

  function openFilePicker() {
    if (isDisabled) return;
    fileInputRef.current?.click();
  }

  function handleFileChange(file: File | undefined) {
    if (!file || isDisabled) return;
    onFileSelect(file);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (isDisabled) return;
    const file = event.dataTransfer.files[0];
    if (file) handleFileChange(file);
  }

  return (
    <section className="rounded-xl border border-border/60 bg-card p-5">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(event) => handleFileChange(event.target.files?.[0])}
      />
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors",
          isDisabled
            ? "cursor-not-allowed border-border/40 bg-muted/10 opacity-60"
            : "cursor-pointer border-border/70 bg-muted/20 hover:border-primary/40 hover:bg-muted/30",
        )}
        onClick={openFilePicker}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openFilePicker();
          }
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FileSpreadsheet className="h-6 w-6" aria-hidden />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {isParsing
              ? t("marketplace.admin.import.parsing")
              : t("marketplace.admin.import.uploadDropTitle")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("marketplace.admin.import.uploadDropHint")}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={isDisabled}
          onClick={(event) => {
            event.stopPropagation();
            openFilePicker();
          }}
        >
          <Upload className="mr-2 h-4 w-4" />
          {t("marketplace.admin.import.uploadAction")}
        </Button>
      </div>
      {!canParse ? (
        <p className="mt-3 text-sm text-muted-foreground">{t("common.noPermission")}</p>
      ) : null}
    </section>
  );
}
